import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { LedgerSlotInput, LedgerSource, LedgerTransformSummary, transformLedgers } from '../../api/batch7Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Choose the source system (Xero, QuickBooks or Sage) and how many year files you want to process (1-3).',
  'For each year slot, upload the Account Transactions / NL file. QuickBooks can also take the previous-year Trial Balance.',
  'Click Transform and Build Combined Output. One workbook with a sheet per year downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

interface Slot {
  label: string;
  sageSheet: string;
  tx: File[];
  tb: File[];
}

const emptySlot = (): Slot => ({ label: '', sageSheet: '', tx: [], tb: [] });
const SOURCES: { key: LedgerSource; label: string }[] = [
  { key: 'xero', label: 'Xero' },
  { key: 'qb', label: 'QB' },
  { key: 'sage', label: 'Sage' },
];

function LedgerTransformerPanel({ mission, onBack }: PanelProps) {
  const [source, setSource] = useState<LedgerSource>('xero');
  const [slots, setSlots] = useState<Slot[]>([emptySlot()]);
  const { status, error, result, fail, run } = useFileRun<FileResult<LedgerTransformSummary>>();

  const setCount = (n: number) =>
    setSlots((cur) => Array.from({ length: n }, (_, i) => cur[i] ?? emptySlot()));

  const update = (i: number, patch: Partial<Slot>) =>
    setSlots((cur) => cur.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const handleSubmit = async () => {
    if (!slots.some((s) => s.tx[0])) {
      fail('Upload at least one Account Transactions file.');
      return;
    }
    const input: LedgerSlotInput[] = slots.map((s) => ({
      label: s.label.trim(),
      sageSheet: s.sageSheet.trim(),
      tx: s.tx[0] ?? null,
      tb: s.tb[0] ?? null,
    }));
    await run(() => transformLedgers(source, input), 'Transformation failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="mb-5 flex flex-wrap items-end gap-6">
        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground">Source type</p>
          <div className="flex gap-2">
            {SOURCES.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSource(s.key)}
                className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                  source === s.key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="lt-slots" className="mb-1.5">
            Year files to process
          </Label>
          <select
            id="lt-slots"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
            value={slots.length}
            onChange={(e) => setCount(Number(e.target.value))}
          >
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {slots.map((slot, i) => (
        <div key={i} className="mb-5 rounded-lg border border-border p-4">
          <h3 className="mb-3 text-sm font-bold text-foreground">Year slot {i + 1}</h3>
          <div className="mb-3">
            <Label htmlFor={`lt-label-${i}`} className="mb-1.5">
              Optional sheet name
            </Label>
            <Input
              id={`lt-label-${i}`}
              value={slot.label}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="Defaults to the year in the file name"
            />
          </div>
          <FileDrop
            label="Account Transactions / NL file"
            accept={mission.accept}
            hint={mission.maxSizeLabel}
            accentColor={mission.color}
            files={slot.tx}
            onFiles={(f) => update(i, { tx: f })}
          />
          {source === 'qb' && (
            <FileDrop
              label="Previous-year Trial Balance (optional)"
              note="Without it, fallback sign logic is used."
              accept={mission.accept}
              hint={mission.maxSizeLabel}
              accentColor={mission.color}
              files={slot.tb}
              onFiles={(f) => update(i, { tb: f })}
            />
          )}
          {source === 'sage' && (
            <div>
              <Label htmlFor={`lt-sage-${i}`} className="mb-1.5">
                Sage source worksheet (optional)
              </Label>
              <Input
                id={`lt-sage-${i}`}
                value={slot.sageSheet}
                onChange={(e) => update(i, { sageSheet: e.target.value })}
                placeholder="Defaults to the first sheet"
              />
            </div>
          )}
        </div>
      ))}

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Building…' : 'Transform and Build Combined Output'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Building…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          <ul className="mt-1 list-disc pl-5 text-xs text-green-800">
            {result.summary?.slots.map((s) => (
              <li key={s.slot}>
                Slot {s.slot}: {s.file} → sheet "{s.sheet}" ({s.method})
              </li>
            ))}
          </ul>
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>Upload at least one Account Transactions file to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default LedgerTransformerPanel;
